/**
 * @file 教材の表紙画像の URL。
 *
 * @what: ISBN から Amazon の書影 (`images-na.ssl-images-amazon.com/images/P/<ISBN-10>.09.LZZZZZZZ.jpg`)
 *   を引く。openBD の書影はこぐま会・理英会など直販中心の出版社で無いことが多く、Amazon の
 *   ISBN 指定の書影がいちばん揃う (2026-08-24 に 2 社で確認)。
 * @why: 教材の一覧で表紙が見えると、買うとき・本棚で探すときに迷わない (代表 2026-08-24)。
 * @failure: ISBN が無い / 10 桁に直せない → null (表紙の枠だけ出す)。Amazon に書影が無いときは
 *   1×1 の画像が返るので、読み込み後に naturalWidth で判定して枠に戻す (MaterialCover)。
 */

/** ISBN-13 (978 接頭) を ISBN-10 に直す。979 接頭は 10 桁にできないので null。 */
export function isbn13To10(isbn13: string): string | null {
  const s = isbn13.replace(/[-\s]/g, "");
  if (!/^978[0-9]{10}$/.test(s)) return null;
  const core = s.slice(3, 12);
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(core[i]) * (10 - i);
  const r = (11 - (sum % 11)) % 11;
  return core + (r === 10 ? "X" : String(r));
}

export const AMAZON_COVER_ORIGIN = "https://images-na.ssl-images-amazon.com";

export function coverUrl(isbn: string | undefined): string | null {
  if (!isbn) return null;
  const isbn10 = isbn.length === 10 ? isbn.toUpperCase() : isbn13To10(isbn);
  if (!isbn10) return null;
  return `${AMAZON_COVER_ORIGIN}/images/P/${isbn10}.09.LZZZZZZZ.jpg`;
}

/**
 * 商品ページ URL (Amazon の /dp/) から書影コード (ASIN) を取り出す。
 * @why: 七田式プリントのような ISBN の無い教材セットは ASIN でしか書影を引けない
 *   (代表 2026-08-24)。Amazon の書影 API は ISBN-10 と同じ形式で ASIN も受ける。
 * @failure: Amazon 以外の URL・/dp/ の無い URL は null (表紙は頭文字の枠になる)。
 */
export function asinFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  const m = /^https:\/\/www\.amazon\.(?:co\.jp|com)\/(?:.*\/)?dp\/([a-z0-9]{10})(?:[/?#]|$)/i.exec(url);
  // 書影 API のコードは大文字 (ISBN-10 の X と同じ扱い)
  return m?.[1]?.toUpperCase() ?? null;
}

/**
 * 教材の表紙 URL。
 * @what: ISBN があれば ISBN-10 の書影、無ければ商品 URL の ASIN の書影を返す。
 * @why: ISBN のある本は ISBN が正 (書誌として安定)。ISBN の無い教材セットだけ ASIN に落とす。
 * @failure: どちらも引けなければ null (表紙は頭文字の枠になる)。
 */
export function materialCoverUrl(m: { isbn?: string; url?: string }): string | null {
  const byIsbn = coverUrl(m.isbn);
  if (byIsbn) return byIsbn;
  const asin = asinFromUrl(m.url);
  return asin ? `${AMAZON_COVER_ORIGIN}/images/P/${asin}.09.LZZZZZZZ.jpg` : null;
}
